import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { transcribeVoiceMemo } from "@/lib/relationships.functions";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

function pickMime(): string {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

export function VoiceMemoButton({
  contactId,
  calendarEventId,
  onSuccess,
  iconOnly,
}: {
  contactId: string;
  calendarEventId?: string;
  onSuccess?: (result: { transcript: string; facts: string[] }) => void;
  iconOnly?: boolean;
}) {
  const [supported, setSupported] = useState(false);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const tickRef = useRef<number | null>(null);
  const transcribe = useServerFn(transcribeVoiceMemo);
  const qc = useQueryClient();

  useEffect(() => {
    setSupported(
      typeof navigator !== "undefined" &&
        !!navigator.mediaDevices?.getUserMedia &&
        typeof MediaRecorder !== "undefined" &&
        !!pickMime(),
    );
  }, []);

  const mut = useMutation({
    mutationFn: (p: { audioBase64: string; mimeType: string; durationSec: number }) =>
      transcribe({ data: { contactId, calendarEventId, ...p } }),
    onSuccess: ({ count, transcript, facts }) => {
      if (!transcript) {
        toast.error("Couldn't hear anything — try again");
        return;
      }
      qc.invalidateQueries({ queryKey: ["contact", contactId] });
      qc.invalidateQueries({ queryKey: ["home-digest"] });
      qc.invalidateQueries({ queryKey: ["pending-hooks"] });
      qc.invalidateQueries({ queryKey: ["people"] });
      qc.invalidateQueries({ queryKey: ["next-touch-note", contactId] });
      toast.success(count > 1 ? `Saved ${count} facts from voice memo` : "Voice memo saved");
      onSuccess?.({ transcript, facts: facts ?? [] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Couldn't transcribe"),
  });

  function cleanup() {
    if (tickRef.current != null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
    setSeconds(0);
    setRecording(false);
  }

  async function start() {
    try {
      const mimeType = pickMime();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const type = rec.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const durationSec = seconds;
        cleanup();
        if (blob.size < 1000) {
          toast.error("Recording too short");
          return;
        }
        if (blob.size > 6_000_000) {
          toast.error("Recording too long — keep it under ~2 minutes");
          return;
        }
        const audioBase64 = await blobToBase64(blob);
        mut.mutate({ audioBase64, mimeType: type, durationSec });
      };
      rec.start();
      setRecording(true);
      setSeconds(0);
      tickRef.current = window.setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= 120) {
            // hard cap at 2 minutes
            try { rec.stop(); } catch {}
            return s;
          }
          return s + 1;
        });
      }, 1000);
    } catch (e: any) {
      cleanup();
      toast.error(e?.message ?? "Couldn't access mic");
    }
  }

  function stop() {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
    else cleanup();
  }

  useEffect(() => {
    return () => cleanup();
  }, []);

  if (!supported) return null;

  const busy = mut.isPending;
  const mm = String(Math.floor(seconds / 60)).padStart(1, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  if (iconOnly) {
    return (
      <button
        type="button"
        onClick={recording ? stop : start}
        disabled={busy}
        className={`grid h-8 w-8 place-items-center rounded-full transition disabled:opacity-50 ${
          recording
            ? "bg-destructive text-destructive-foreground"
            : "glass-soft text-muted-foreground hover:text-primary"
        }`}
        aria-label={busy ? "Transcribing" : recording ? `Stop recording (${mm}:${ss})` : "Record voice memo"}
        title={busy ? "Transcribing…" : recording ? `Stop · ${mm}:${ss}` : "Voice memo"}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : recording ? (
          <Square className="h-3 w-3 fill-current" />
        ) : (
          <Mic className="h-3.5 w-3.5" />
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={busy}
      className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition ${
        recording
          ? "bg-destructive text-destructive-foreground"
          : "glass-soft text-foreground hover:text-primary"
      } disabled:opacity-50`}
      aria-label={recording ? "Stop voice memo" : "Record voice memo"}
    >
      {busy ? (
        <>
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Transcribing…
        </>
      ) : recording ? (
        <>
          <Square className="h-3 w-3 fill-current" /> Stop · {mm}:{ss}
        </>
      ) : (
        <>
          <Mic className="h-3.5 w-3.5" /> Voice memo
        </>
      )}
    </button>
  );
}

export function VoiceMemoInline({
  contactId,
  calendarEventId,
  onSuccess,
}: {
  contactId: string;
  calendarEventId?: string;
  onSuccess?: (result: { transcript: string; facts: string[] }) => void;
}) {
  const [supported, setSupported] = useState(false);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const tickRef = useRef<number | null>(null);
  const transcribe = useServerFn(transcribeVoiceMemo);
  const qc = useQueryClient();

  useEffect(() => {
    setSupported(
      typeof navigator !== "undefined" &&
        !!navigator.mediaDevices?.getUserMedia &&
        typeof MediaRecorder !== "undefined" &&
        !!pickMime(),
    );
  }, []);

  const mut = useMutation({
    mutationFn: (p: { audioBase64: string; mimeType: string; durationSec: number }) =>
      transcribe({ data: { contactId, calendarEventId, ...p } }),
    onSuccess: ({ count, transcript, facts }) => {
      if (!transcript) {
        toast.error("Couldn't hear anything — try again");
        return;
      }
      qc.invalidateQueries({ queryKey: ["contact", contactId] });
      qc.invalidateQueries({ queryKey: ["home-digest"] });
      qc.invalidateQueries({ queryKey: ["pending-hooks"] });
      qc.invalidateQueries({ queryKey: ["people"] });
      qc.invalidateQueries({ queryKey: ["next-touch-note", contactId] });
      toast.success(count > 1 ? `Saved ${count} facts from voice memo` : "Voice memo saved");
      onSuccess?.({ transcript, facts: facts ?? [] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Couldn't transcribe"),
  });

  function cleanup() {
    if (tickRef.current != null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
    setSeconds(0);
    setRecording(false);
  }

  async function start() {
    try {
      const mimeType = pickMime();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const type = rec.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const durationSec = seconds;
        cleanup();
        if (blob.size < 1000) {
          toast.error("Recording too short");
          return;
        }
        if (blob.size > 6_000_000) {
          toast.error("Recording too long — keep it under ~2 minutes");
          return;
        }
        const audioBase64 = await blobToBase64(blob);
        mut.mutate({ audioBase64, mimeType: type, durationSec });
      };
      rec.start();
      setRecording(true);
      setSeconds(0);
      tickRef.current = window.setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= 120) {
            try { rec.stop(); } catch {}
            return s;
          }
          return s + 1;
        });
      }, 1000);
    } catch (e: any) {
      cleanup();
      toast.error(e?.message ?? "Couldn't access mic");
    }
  }

  function stop() {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
    else cleanup();
  }

  useEffect(() => () => cleanup(), []);

  if (!supported) {
    return (
      <p className="text-center text-xs text-muted-foreground py-8">
        Recording isn't supported in this browser. Use the Write tab.
      </p>
    );
  }

  const busy = mut.isPending;
  const mm = String(Math.floor(seconds / 60)).padStart(1, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="flex flex-col items-center justify-center py-6 gap-4">
      <button
        type="button"
        onClick={recording ? stop : start}
        disabled={busy}
        aria-label={busy ? "Transcribing" : recording ? "Stop recording" : "Start recording"}
        className={`grid h-24 w-24 place-items-center rounded-full transition disabled:opacity-50 ring-1 ${
          recording
            ? "bg-destructive text-destructive-foreground ring-destructive/50 animate-pulse"
            : "bg-primary text-primary-foreground ring-primary/40 hover:scale-105"
        }`}
      >
        {busy ? (
          <Loader2 className="h-8 w-8 animate-spin" />
        ) : recording ? (
          <Square className="h-8 w-8 fill-current" />
        ) : (
          <Mic className="h-9 w-9" />
        )}
      </button>
      <div className="text-center">
        {busy ? (
          <p className="text-sm text-muted-foreground">Transcribing…</p>
        ) : recording ? (
          <>
            <p className="text-2xl font-mono tabular-nums">{mm}:{ss}</p>
            <p className="text-xs text-muted-foreground mt-1">Tap to stop · 2 min max</p>
          </>
        ) : (
          <>
            <p className="text-sm font-medium">Tap to record</p>
            <p className="text-xs text-muted-foreground mt-1">Pyng will transcribe and split into facts</p>
          </>
        )}
      </div>
    </div>
  );
}
